import React, { useEffect, useState } from 'react';
import { StanButton } from '@/components';
import { CancellationModalWrapper } from './CancellationModalWrapper';
import { GOODBYE_MODAL_STRINGS } from '../../constants/cancellationStrings';

interface GoodbyeModalProps {
    isOpen: boolean;
    onClose: () => void;
    onEmojiClick?: (satisfaction: string) => void;
    validUntilDate?: string;
}

type SatisfactionLevel = 'bad' | 'okay' | 'good' | null;

interface EmojiButtonProps {
    emoji: string;
    level: SatisfactionLevel;
    onClick: (level: SatisfactionLevel) => void;
}

const EmojiButton = ({ emoji, level, onClick }: EmojiButtonProps) => (
    <button
        type='button'
        onClick={() => onClick(level)}
        className='cursor-pointer rounded-lg p-2 text-6xl transition-all hover:opacity-70'
    >
        {emoji}
    </button>
);

export const GoodbyeModal = ({ isOpen, onClose, onEmojiClick, validUntilDate }: GoodbyeModalProps) => {
    const [showThankYou, setShowThankYou] = useState(false);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setShowThankYou(false);
        }
    }, [isOpen]);

    const handleEmojiClick = (level: SatisfactionLevel) => {
        if (level) {
            setShowThankYou(true);
            // Notify parent if callback provided
            if (onEmojiClick) {
                onEmojiClick(level);
            }
        }
    };

    return (
        <CancellationModalWrapper isOpen={isOpen} showNav={false} centered>
            <div className='flex w-full flex-col items-center space-y-8 text-center'>
                {/* Title */}
                <h1 className='text-heading-heading1'>{GOODBYE_MODAL_STRINGS.TITLE}</h1>

                {/* Description */}
                <p className='text-body-1-regular'>
                    {GOODBYE_MODAL_STRINGS.DESCRIPTION_PREFIX} <span className='font-bold'>{validUntilDate}</span>
                    {GOODBYE_MODAL_STRINGS.DESCRIPTION_SUFFIX}{' '}
                    <a
                        href={`mailto:${GOODBYE_MODAL_STRINGS.SUPPORT_EMAIL}`}
                        className='text-body-1-bold text-brand-main'
                    >
                        {GOODBYE_MODAL_STRINGS.SUPPORT_EMAIL}
                    </a>
                </p>

                {/* Emoji survey or Thank you message */}
                {!showThankYou ? (
                    <div className='bg-brand-dimmed w-full max-w-sm rounded-lg p-6'>
                        <h3 className='text-heading-heading3 mb-4 whitespace-pre-line'>
                            {GOODBYE_MODAL_STRINGS.SURVEY.TITLE}
                        </h3>
                        <div className='flex items-center justify-center gap-6'>
                            <EmojiButton emoji='🙁' level='bad' onClick={handleEmojiClick} />
                            <EmojiButton emoji='😐' level='okay' onClick={handleEmojiClick} />
                            <EmojiButton emoji='🙂' level='good' onClick={handleEmojiClick} />
                        </div>
                    </div>
                ) : (
                    <div className='bg-brand-dimmed w-full max-w-sm rounded-lg p-6'>
                        <div className='mb-2 text-2xl'>💜</div>
                        <p className='text-body-2-bold'>{GOODBYE_MODAL_STRINGS.SURVEY.THANK_YOU}</p>
                    </div>
                )}

                {/* Close button */}
                <StanButton name='goodbye-close-button' size='sm' onClick={onClose} variant='ghost'>
                    {GOODBYE_MODAL_STRINGS.BUTTON_LABEL}
                </StanButton>
            </div>
        </CancellationModalWrapper>
    );
};
